/**
 * Maps HTTP errors to AppError with proper categorization
 * Handles error extraction from backend responses
 */

import type { HttpError } from '../types/httpClient.interface';
import { AppError } from '../../../types/errors.interface';
import { ErrorCodes } from '../../../constants/errorCodes';
import { CLIENT_ERROR_CODES } from '../../../constants/clientErrorCodes';
import { createAppError } from '../../../utils/errorCategoryMapper';
import { extractFirstBackendError } from '../../../utils/backendErrorExtractor';

/**
 * Type guard to check if an error is an HttpError
 */
export function isHttpError(error: unknown): error is HttpError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'isHttpError' in error &&
    (error as HttpError).isHttpError === true
  );
}

/**
 * Maps HTTP status code and response data to AppError
 * Prioritizes backend error codes if available
 * 
 * @param status - HTTP status code
 * @param statusText - HTTP status text
 * @param responseData - Response body data
 * @returns Properly categorized AppError
 */
export function mapHttpErrorToAppError(
  status: number,
  statusText: string,
  responseData: unknown
): AppError {
  // Try to extract backend error code first
  const backendError = extractFirstBackendError(responseData);
  
  if (backendError) {
    // Use backend error code and message
    return createAppError(
      backendError.code,
      backendError.message,
      backendError.field,
      status
    );
  }

  // No backend error code, map based on HTTP status
  return mapHttpStatusToAppError(status, statusText);
}

/**
 * Maps HTTP status code to AppError when no backend error code is available
 * 
 * @param status - HTTP status code
 * @param statusText - HTTP status text
 * @returns Categorized AppError
 */
export function mapHttpStatusToAppError(status: number, statusText: string): AppError {
  // 401 - Unauthenticated (not logged in)
  if (status === 401) {
    return {
      category: 'auth',
      code: ErrorCodes.AUTH_UNAUTHENTICATED,
      message: 'Your session has expired. Please log in again.',
      httpStatus: 401,
      shouldLogout: true,
      redirectToLogin: true,
    };
  }

  // 403 - Unauthorized (logged in but forbidden)
  if (status === 403) {
    return {
      category: 'auth',
      code: ErrorCodes.AUTH_UNAUTHORIZED,
      message: 'You do not have permission to access this resource.',
      httpStatus: 403,
      shouldLogout: false,
      redirectToLogin: false,
    };
  }

  // 400 - Rejected before any handler ran (route/model binding), so the body carries
  // no error code. Without this case it fell through to the generic 4xx branch and
  // surfaced as a business failure it is not.
  if (status === 400) {
    return {
      category: 'validation',
      code: CLIENT_ERROR_CODES.BAD_REQUEST,
      message: 'The request was rejected. Please check your entries and try again.',
    };
  }

  // 422 - Validation error
  if (status === 422) {
    return {
      category: 'validation',
      code: ErrorCodes.VAL_REQUIRED_FIELD,
      message: 'Validation failed. Please check your input.',
    };
  }

  // 404 - Not found
  if (status === 404) {
    return {
      category: 'business',
      code: ErrorCodes.BUS_NOT_FOUND,
      message: 'The requested resource was not found.',
    };
  }

  // 409 - Conflict
  if (status === 409) {
    return {
      category: 'business',
      code: ErrorCodes.BUS_CONFLICT,
      message: 'This operation conflicts with existing data.',
    };
  }

  // 5xx - Server errors
  if (status >= 500) {
    return {
      category: 'server',
      code: ErrorCodes.SRV_INTERNAL_ERROR,
      message: `Server error: ${statusText || 'Internal server error'}`,
      statusCode: status,
    };
  }

  // 4xx - Other client errors
  if (status >= 400) {
    return {
      category: 'business',
      code: ErrorCodes.BUS_OPERATION_FAILED,
      message: `Request failed: ${statusText || 'Bad request'}`,
    };
  }

  // Fallback for unexpected status codes
  return {
    category: 'server',
    code: CLIENT_ERROR_CODES.UNKNOWN_ERROR,
    message: `Unexpected HTTP status: ${status} ${statusText}`,
    statusCode: status,
  };
}

/**
 * Maps any error (HttpError or network error) to AppError
 * Used by API layer to convert caught exceptions to AppError
 * 
 * @param error - Error caught from HTTP call
 * @returns Properly typed AppError
 */
export function mapHttpExceptionToAppError(error: unknown): AppError {
  // Check if it's an HttpError with response data
  if (isHttpError(error) && error.response) {
    return mapHttpErrorToAppError(
      error.response.status,
      error.response.statusText,
      error.response.data
    );
  }

  // Otherwise, treat as network error
  return mapNetworkErrorToAppError(error);
}

/**
 * Maps network/connectivity errors to AppError
 * 
 * @param error - Native error object
 * @returns Network error
 */
export function mapNetworkErrorToAppError(error: unknown): AppError {
  const errorMessage = error instanceof Error ? error.message : 'Unknown network error';
  const errorName = error instanceof Error ? error.name : '';

  // Timeout errors
  if (errorMessage.includes('timeout') || errorMessage.includes('ECONNABORTED')) {
    return {
      category: 'network',
      code: CLIENT_ERROR_CODES.NETWORK_TIMEOUT,
      message: 'The request timed out. Please check your connection and try again.',
    };
  }

  // Aborted/cancelled requests
  if (errorName === 'AbortError' || errorMessage.includes('aborted')) {
    return {
      category: 'network',
      code: CLIENT_ERROR_CODES.NETWORK_ABORTED,
      message: 'The request was cancelled.',
    };
  }

  // Connection refused/offline
  if (
    errorMessage.includes('Network Error') ||
    errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('ECONNREFUSED')
  ) {
    return {
      category: 'network',
      code: CLIENT_ERROR_CODES.NETWORK_OFFLINE,
      message: 'Could not connect to the server. Please check your internet connection.',
    };
  }

  // Generic network error
  return {
    category: 'network',
    code: CLIENT_ERROR_CODES.UNKNOWN_ERROR,
    message: errorMessage || 'A network error occurred.',
  };
}
